import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plug, LayoutPanelLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { CountryMultiSelect } from "../components/CountryMultiSelect";
import { errMsg } from "../lib/api";
import { PageHero } from "../components/PageHero";

export default function Partners() {
  const { supplierApply } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", password: "", website: "", integration: "portal", api_notes: "", countries: [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.countries.length) return toast.error("Choose at least one country");
    setBusy(true);
    try {
      await supplierApply(f);
      toast.success("Application received. Add your packages while we review.");
      nav("/supplier");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="partners-page">
      <PageHero eyebrow="For ISPs & wholesalers" title="Sell your network through HostAfricair." tone="sky"
        sub="We bring the customers, billing and support. You supply the connectivity at your wholesale price.">
        <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
          {[[LayoutPanelLeft, "Supplier portal", "Add packages and coverage areas, receive orders and update installation status."],
            [Plug, "API integration", "Have a reseller API? Orders, coverage checks and domains flow automatically."]].map(([I, t, d]) => (
            <div key={t} className="rounded-3xl bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1"><span className="grid h-10 w-10 place-items-center rounded-2xl bg-accent"><I className="h-5 w-5 text-primary" /></span><p className="mt-3 font-bold">{t}</p><p className="mt-1 text-sm text-muted-foreground">{d}</p></div>
          ))}
        </div>
      </PageHero>
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <form onSubmit={submit} className="space-y-5 rounded-3xl border bg-white p-6 shadow-[0_30px_60px_-40px_rgba(27,16,51,0.4)] sm:p-8" data-testid="supplier-apply-form">
          <h2 className="text-2xl font-bold">Apply as a supplier</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Company name</Label><Input className="mt-1.5" required value={f.company_name} onChange={set("company_name")} data-testid="apply-company-input" /></div>
            <div><Label>Contact name</Label><Input className="mt-1.5" required value={f.contact_name} onChange={set("contact_name")} data-testid="apply-contact-input" /></div>
            <div><Label>Work email</Label><Input className="mt-1.5" type="email" required value={f.email} onChange={set("email")} data-testid="apply-email-input" /></div>
            <div><Label>Password</Label><Input className="mt-1.5" type="password" minLength={6} required value={f.password} onChange={set("password")} data-testid="apply-password-input" /></div>
            <div className="sm:col-span-2"><Label>Website</Label><Input className="mt-1.5" value={f.website} onChange={set("website")} data-testid="apply-website-input" /></div>
          </div>
          <div><Label>Countries you serve</Label><div className="mt-1.5"><CountryMultiSelect value={f.countries} onChange={(countries) => setF({ ...f, countries })} /></div></div>
          <div>
            <Label>How will you connect?</Label>
            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
              {[["portal", "Supplier portal"], ["api", "API integration"]].map(([k, l]) => (
                <button type="button" key={k} onClick={() => setF({ ...f, integration: k })} data-testid={`apply-integration-${k}`}
                  className={`flex items-center gap-2 rounded-md border p-3 text-left text-sm font-medium transition-colors ${f.integration === k ? "border-primary bg-accent" : "hover:bg-muted"}`}>
                  {f.integration === k && <CheckCircle2 className="h-4 w-4 text-primary" />}{l}
                </button>
              ))}
            </div>
          </div>
          {f.integration === "api" && <div><Label>Tell us about your API</Label><Textarea className="mt-1.5" value={f.api_notes} onChange={set("api_notes")} data-testid="apply-api-notes" placeholder="Docs link, endpoints for orders/coverage/domains…" /></div>}
          <Button className="h-11 w-full" disabled={busy} data-testid="apply-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}</Button>
        </form>
      </section>
    </div>
  );
}
